const PROJECT_SESSION_KEYS = [
  'project_id',
  'selected_project_id',
  'current_project_id',
  'active_project_id',
];

export const EVENT_FILTERS = [
  'All events',
  'Case events',
  'Notes',
  'Rules',
  'Timesheets',
  'Exports',
  'Settings',
];
export const ALL_TIME_RANGE_FILTER = 'All time';
export const RANGE_FILTERS = [ALL_TIME_RANGE_FILTER, 'Last 7 days', 'Last 30 days', 'Last 90 days'];
export const ALL_STATUS_FILTER = 'All statuses';
export const clone = (value) => JSON.parse(JSON.stringify(value));

function normalize_text(value) {
  return String(value || '').trim();
}

export function to_number(value) {
  const normalized = Number(value);
  return Number.isFinite(normalized) && normalized > 0 ? normalized : null;
}

export function read_session_project_id() {
  if (typeof window === 'undefined') return null;

  for (const key of PROJECT_SESSION_KEYS) {
    const project_id = to_number(window.sessionStorage.getItem(key));
    if (project_id) return project_id;
  }
  return null;
}

function first_present(values = []) {
  return values.find((value) => value !== undefined && value !== null && value !== '') ?? null;
}

function status_name(status) {
  if (!status) return '';
  return normalize_text([status?.name, status?.status, status?.status_name, status].find(Boolean));
}

function audit_timestamp_raw(log = {}) {
  return first_present([
    log?.timestamp,
    log?.date_created,
    log?.created_at,
    log?.updated_at,
    log?.when,
  ]);
}

function audit_timestamp_value(log = {}) {
  const date = new Date(audit_timestamp_raw(log));
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function format_audit_timestamp(value) {
  if (!value) return '';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return normalize_text(value);

  const date_part = date.toLocaleDateString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const time_part = date.toLocaleTimeString('en-ZA', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  return `${date_part} ${time_part}`;
}

function title_case(value = '') {
  return normalize_text(value)
    .toLowerCase()
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function normalize_action(value = '') {
  return normalize_text(value).replace(/\s+/g, '_').replace(/-+/g, '_').toUpperCase();
}

function action_label(value = '') {
  return title_case(normalize_action(value));
}

function event_key_for_log(log = {}, action = '', details = '') {
  if (note_audit_type(details)) {
    return 'Notes';
  }

  const haystack = normalize_text([
    action,
    details,
    log?.event_key,
    log?.event_type,
    log?.log_type,
    log?.category,
    log?.module,
    log?.target,
  ].join(' ')).toLowerCase();

  return EVENT_RULES.find(({ keys }) => keys.some((key) => haystack.includes(key)))?.event_key
    || 'Case events';
}

const EVENT_RULES = [
  { event_key: 'Rules', keys: ['rule'] },
  { event_key: 'Timesheets', keys: ['timesheet', 'time sheet', 'journal'] },
  { event_key: 'Exports', keys: ['export', 'download'] },
  { event_key: 'Settings', keys: ['setting', 'tenant', 'configuration'] },
];

const ACTION_BADGES = [
  {
    keys: ['delete', 'decline', 'overturn'],
    class_name: 'bg-danger-subtle text-danger-emphasis border border-danger-subtle',
  },
  {
    keys: ['approve', 'complete', 'clear'],
    class_name: 'bg-success-subtle text-success-emphasis border border-success-subtle',
  },
  {
    keys: ['assign', 'upload', 'create'],
    class_name: 'bg-primary-subtle text-primary-emphasis border border-primary-subtle',
  },
];
const EVENT_BADGES = new Map([
  ['Notes', 'bg-primary-subtle text-primary-emphasis border border-primary-subtle'],
  ['Rules', 'bg-info-subtle text-info-emphasis border border-info-subtle'],
  ['Timesheets', 'bg-success-subtle text-success-emphasis border border-success-subtle'],
  ['Exports', 'bg-warning-subtle text-warning-emphasis border border-warning-subtle'],
  ['Settings', 'text-dark-purple bg-light-purple border border-purple'],
]);
const ASSIGNMENT_COLLECTION_FIELDS = [
  'application_assigned_users',
  'assigned_users',
  'assignments',
  'assigned_panel_members',
];
const ASSIGNMENT_NAME_FIELDS = [
  'assigned_to_name',
  'routed_to_name',
  'target_user_name',
  'assignee_name',
  'user_name',
  'full_name',
  'name',
  'assigned_to_email',
  'email',
  'owner',
  'handler',
  'assigned_to',
  'assignee',
];
const ROUTED_TO_FIELDS = [
  'routed_to',
  'routed_to_name',
  'routed_to_users',
  'assigned_to',
  'assigned_to_name',
  'assigned_users',
  'application_assigned_users',
  'assignee',
  'assignee_name',
  'owner',
  'handler',
  'target_user',
  'target_user_name',
];

function action_badge_class(event_key = '', action = '') {
  const normalized_action = action.toLowerCase();
  const action_badge = ACTION_BADGES.find(({ keys }) =>
    keys.some((key) => normalized_action.includes(key))
  );
  return action_badge?.class_name
    || EVENT_BADGES.get(event_key)
    || 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle';
}

function actor_label(log = {}) {
  const full_name = [log?.user_first_name, log?.user_last_name]
    .map(normalize_text)
    .filter(Boolean)
    .join(' ');
  const actors = [
    log?.actor,
    log?.user_full_name,
    full_name,
    log?.username,
    log?.email,
    log?.reviewer,
    log?.reviewer_name,
    log?.user_role,
    log?.role,
    log?.role_name,
    'SYSTEM',
  ];
  return title_case(actors.find(Boolean) || 'SYSTEM');
}

function actor_key(log = {}, actor = '') {
  const identifiers = [
    log?.user_id,
    log?.reviewer_id,
    log?.actor_id,
    log?.profile_id,
    log?.email,
    actor,
  ];
  return normalize_text(identifiers.find(Boolean)).toLowerCase();
}

function normalize_role(value) {
  if (!value) return '';
  if (typeof value === 'object') {
    return normalize_text(value?.name || value?.role || value?.role_name || value?.label);
  }
  return normalize_text(value);
}

function actor_role_label(log = {}) {
  const roles = [
    log?.actor_role,
    log?.user_role,
    log?.reviewer_role,
    log?.role_name,
    log?.role,
  ].map(normalize_role);
  return title_case(roles.find(Boolean) || '');
}

function normalize_email(value = '') {
  const normalized = normalize_text(value).toLowerCase();
  return normalized.includes('@') ? normalized : '';
}

function actor_email_label(log = {}) {
  const emails = [
    log?.actor_email,
    log?.user_email,
    log?.reviewer_email,
    log?.email,
    log?.username,
  ].map(normalize_email);
  return emails.find(Boolean) || '';
}

function actor_filter_key(actor = '', role = '', email = '') {
  return normalize_text([actor, role, email].filter(Boolean).join('::')).toLowerCase();
}

function looks_like_case_code(value = '') {
  return /^C-\d{4}-\d+$/i.test(normalize_text(value));
}

function looks_like_raw_numeric_id(value = '') {
  return /^\d+$/.test(normalize_text(value));
}

function safe_target_text(value = '') {
  const normalized = normalize_text(value);
  return normalized && !looks_like_raw_numeric_id(normalized) ? normalized : '';
}

function case_lookup_key(prefix = '', value = null) {
  const normalized = to_number(value);
  return normalized ? `${prefix}:${normalized}` : '';
}

function case_code_lookup_key(value = '') {
  const normalized = normalize_text(value).toLowerCase();
  return looks_like_case_code(normalized) ? `case:${normalized}` : '';
}

function unique_labels(values = []) {
  const seen = new Set();

  return values.filter((value) => {
    const label = normalize_text(value);
    const key = label.toLowerCase();

    if (!label || key === 'unassigned' || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function user_label(value) {
  if (Array.isArray(value)) {
    return unique_labels(value.map(user_label)).join(', ');
  }

  if (!value || typeof value !== 'object') {
    return normalize_text(value);
  }

  const full_name = [value?.first_name, value?.last_name]
    .map(normalize_text)
    .filter(Boolean)
    .join(' ');
  const assigned_full_name = [
    value?.assigned_to?.first_name,
    value?.assigned_to?.last_name,
  ].map(normalize_text).filter(Boolean).join(' ');

  return normalize_text(first_present([
    value?.assigned_to_name,
    value?.routed_to_name,
    value?.target_user_name,
    value?.assignee_name,
    value?.user_name,
    value?.full_name,
    full_name,
    value?.name,
    value?.assigned_to?.full_name,
    assigned_full_name,
    value?.assigned_to?.name,
    value?.assigned_to?.email,
    value?.assigned_to_email,
    value?.email,
  ]));
}

function assignment_names(case_item = {}) {
  const collection_names = ASSIGNMENT_COLLECTION_FIELDS
    .flatMap((field) => (Array.isArray(case_item?.[field]) ? case_item[field] : []))
    .map(user_label);
  const direct_names = ASSIGNMENT_NAME_FIELDS.map((field) => user_label(case_item?.[field]));

  return unique_labels([...collection_names, ...direct_names]);
}

function case_lookup_value(case_item = {}, code = '') {
  return {
    case_code: code,
    application_id: to_number(case_item?.application_id),
    entity_profile_id: to_number(case_item?.entity_profile_id),
    entity_type_id: to_number(case_item?.entity_type_id),
    routed_to: assignment_names(case_item).join(', '),
  };
}

function lookup_case_context(case_lookup = new Map(), key = '') {
  const value = key ? case_lookup.get(key) : null;

  if (!value) return null;
  if (typeof value === 'object') return value;

  return {
    case_code: normalize_text(value),
    routed_to: '',
  };
}

export function build_case_lookup(cases = []) {
  const lookup = new Map();

  cases.forEach((case_item) => {
    const code = normalize_text(case_item?.case_code);
    if (!looks_like_case_code(code)) {
      return;
    }

    const application_key = case_lookup_key('application', case_item?.application_id);
    const entity_profile_key = case_lookup_key('entity_profile', case_item?.entity_profile_id);
    const case_key = case_code_lookup_key(code);
    const lookup_value = case_lookup_value(case_item, code);

    if (application_key && !lookup.has(application_key)) {
      lookup.set(application_key, lookup_value);
    }

    if (entity_profile_key && !lookup.has(entity_profile_key)) {
      lookup.set(entity_profile_key, lookup_value);
    }

    if (case_key && !lookup.has(case_key)) {
      lookup.set(case_key, lookup_value);
    }
  });

  return lookup;
}

function direct_case_code(log = {}) {
  const case_codes = [
    log?.reference_number,
    log?.case_code,
    log?.application_code,
    log?.reference,
    log?.code,
    log?.application?.case_code,
    log?.application?.application_code,
    log?.application?.reference_number,
  ];

  return normalize_text(case_codes.find((value) => looks_like_case_code(value)));
}

function case_context_for_log(log = {}, case_lookup = new Map()) {
  const direct_key = case_code_lookup_key(direct_case_code(log));
  return lookup_case_context(case_lookup, direct_key)
    || lookup_case_context(case_lookup, case_lookup_key('application', log?.application_id || log?.application?.id || log?.application))
    || lookup_case_context(case_lookup, case_lookup_key('entity_profile', log?.entity_profile_id || log?.entity_profile?.id));
}

function case_code_label(log = {}, case_lookup = new Map()) {
  const direct_code = direct_case_code(log);
  if (direct_code) return direct_code;

  const case_context = case_context_for_log(log, case_lookup);
  if (case_context?.case_code) return case_context.case_code;

  return '';
}

function target_label(log = {}, case_lookup = new Map()) {
  const case_code = case_code_label(log, case_lookup);
  const targets = [
    case_code,
    safe_target_text(log?.target),
    safe_target_text(log?.object_name),
    safe_target_text(log?.entity_name),
    safe_target_text(log?.application?.name),
    safe_target_text(log?.entity_profile?.name),
    safe_target_text(log?.application),
    safe_target_text(log?.entity_profile),
    '-',
  ];
  return normalize_text(targets.find(Boolean));
}

function case_route(log = {}, case_lookup = new Map()) {
  const case_context = case_context_for_log(log, case_lookup);
  const case_code = case_code_label(log, case_lookup);
  if (!looks_like_case_code(case_code)) return null;

  const query = {
    application_id: to_number(log?.application_id || log?.application?.id) || case_context?.application_id,
    entity_profile_id: to_number(log?.entity_profile_id || log?.entity_profile?.id) || case_context?.entity_profile_id,
    entity_type_id: to_number(log?.entity_type_id || log?.entity_type?.id) || case_context?.entity_type_id,
    tab: 'audit',
  };

  Object.keys(query).forEach((key) => {
    if (!query[key]) delete query[key];
  });

  return {
    name: 'ViewCaseDetails',
    params: { caseCode: case_code_label(log, case_lookup) },
    query,
  };
}

function diff_label(log = {}, action = '') {
  const from_status = status_name([log?.from_status, log?.from_status_label].find(Boolean));
  const to_status = status_name([log?.to_status, log?.to_status_label].find(Boolean));
  const status = status_label(log);
  const details = normalize_text([
    log?.details,
    log?.message,
    log?.note,
    log?.description,
  ].find(Boolean));
  const transition = normalize_text(log?.transition_display);
  const friendly_detail = friendly_audit_detail({
    status,
    from_status,
    to_status,
    transition,
    details,
    action,
  });
  if (friendly_detail) {
    return friendly_detail;
  }

  const status_transition = status_transition_label(from_status, to_status);
  const status_detail = [status, status_transition, transition].find(Boolean);

  return [status_detail, details]
    .filter(Boolean)
    .filter((value) => value !== action)
    .join(' - ') || '-';
}

function status_transition_label(from_status, to_status) {
  return [from_status, to_status].every(Boolean) ? `${from_status} -> ${to_status}` : '';
}

function journal_entry_body(details = '') {
  const match = normalize_text(details).match(/Added journal entry:\s*(.+)$/i);
  return normalize_text(match?.[1]);
}

function is_timesheet_journal(body = '') {
  const normalized = normalize_text(body).toLowerCase();
  return [
    'timesheet',
    'week:',
    'handler:',
    'billable',
    'not billable',
    'hours',
  ].some((token) => normalized.includes(token));
}

function note_audit_type(details = '') {
  const body = journal_entry_body(details);
  if (!body || is_timesheet_journal(body)) return '';

  if (/^\[ReplyTo:/i.test(body) || /^reply to\b/i.test(body)) {
    return 'Reply';
  }

  return /@\S/.test(body) ? 'Mention' : 'Note';
}

function friendly_action_label(action = '', details = '') {
  const note_type = note_audit_type(details);
  if (note_type) return `${note_type} Created`;

  return action_label(action);
}

function source_label(log = {}, actor = '', details = '') {
  const explicit_source = normalize_text(log?.source || log?.source_type || log?.origin);
  if (explicit_source) return title_case(explicit_source);

  const haystack = normalize_text([
    details,
    log?.event,
    log?.event_type,
    log?.log_type,
    log?.category,
  ].join(' ')).toLowerCase();

  if (haystack.includes('auto-transition') || haystack.includes('workflow')) {
    return 'Workflow';
  }

  return normalize_text(actor).toLowerCase() === 'system' ? 'System' : 'User action';
}

function nested_record_value(record = {}, field = '') {
  const details = record?.details;
  if (details && typeof details === 'object' && !Array.isArray(details)) {
    return details[field];
  }

  return undefined;
}

function routed_to_label(log = {}) {
  const routed_value = first_present([
    ...ROUTED_TO_FIELDS.map((field) => log?.[field]),
    ...ROUTED_TO_FIELDS.map((field) => nested_record_value(log, field)),
  ]);
  return user_label(routed_value);
}

function replace_routed_user_count(details = '', routed_to = '') {
  const routed_to_name = normalize_text(routed_to);
  if (!routed_to_name) return normalize_text(details);

  return normalize_text(details)
    .replace(/\bsent to one team member\b/i, `sent to ${routed_to_name}`)
    .replace(/\bsent to \d+ team members\b/i, `sent to ${routed_to_name}`)
    .replace(/\bRouted to \d+\s+user(?:\(s\))?s?\b/i, `Routed to ${routed_to_name}`);
}

function friendly_audit_detail({
  status = '',
  from_status = '',
  to_status = '',
  transition = '',
  details = '',
  action = '',
} = {}) {
  const route_phrase = route_detail_phrase(details);
  const status_sentence = status_change_sentence(from_status, to_status, status, transition);

  if (route_phrase && status_sentence) {
    return `${status_sentence} and ${route_phrase}.`;
  }

  if (route_phrase) {
    return `${sentence_start(route_phrase)}.`;
  }

  if (status_sentence && normalize_text(details) === normalize_text(status)) {
    return `${status_sentence}.`;
  }

  if (details && is_generic_status(status)) {
    return sentence_label(details);
  }

  if (details && normalize_text(details) !== normalize_text(action)) {
    return sentence_label(details);
  }

  return status_sentence ? `${status_sentence}.` : '';
}

function status_change_sentence(from_status = '', to_status = '', status = '', transition = '') {
  if (from_status && to_status) {
    return `Status changed from ${from_status} to ${to_status}`;
  }

  const transition_match = normalize_text(transition).match(/^(.+?)\s*[-=]+>\s*(.+)$/);
  if (transition_match) {
    return `Status changed from ${normalize_text(transition_match[1])} to ${normalize_text(transition_match[2])}`;
  }

  const final_status = normalize_text(to_status || status);
  return final_status && !is_generic_status(final_status)
    ? `Marked as ${final_status.toLowerCase()}`
    : '';
}

function route_detail_phrase(details = '') {
  const normalized = normalize_text(details).replace(/\.$/, '');
  const route_match = normalized.match(/^(?:Routed|Sent) to\s+(.+)$/i);
  if (!route_match) return '';

  const destination = normalize_text(route_match[1]).replace(/\.$/, '');
  const count_match = destination.match(/^(\d+)\s+user(?:\(s\))?s?$/i);
  if (count_match) {
    const count = Number(count_match[1]);
    return count === 1 ? 'sent to one team member' : `sent to ${count} team members`;
  }

  return destination ? `sent to ${destination}` : '';
}

function sentence_label(value = '') {
  const normalized = normalize_text(value);
  if (!normalized) return '';
  return /[.!?]$/.test(normalized) ? normalized : `${normalized}.`;
}

function sentence_start(value = '') {
  const normalized = normalize_text(value);
  return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
}

function is_generic_status(value = '') {
  return ['success', 'error', 'failed', 'failure'].includes(normalize_text(value).toLowerCase());
}

function status_label(log = {}) {
  return status_name(
    [
      log?.status_label,
      log?.status,
      log?.status_name,
      log?.current_status,
      log?.current_status_name,
      log?.workflow_status,
      log?.workflow_status_name,
      log?.application_status,
      log?.application_status_name,
      log?.assigned_form_status,
      log?.assigned_form_status_name,
      log?.to_status,
      log?.to_status_label,
    ].find(Boolean)
  );
}

function status_key(value = '') {
  return normalize_text(value).toLowerCase() || 'unknown';
}

function status_badge_class(value = '') {
  const normalized = status_key(value);

  if (['approved', 'completed', 'verified', 'active', 'resolved', 'closed', 'success'].some((token) => normalized.includes(token))) {
    return 'bg-success-subtle text-success-emphasis border border-success-subtle';
  }

  if (['rejected', 'declined', 'failed', 'error', 'inactive', 'cancelled'].some((token) => normalized.includes(token))) {
    return 'bg-danger-subtle text-danger-emphasis border border-danger-subtle';
  }

  if (['pending', 'review', 'in progress', 'processing', 'queued', 'warning'].some((token) => normalized.includes(token))) {
    return 'bg-warning-subtle text-warning-emphasis border border-warning-subtle';
  }

  return 'bg-secondary-subtle text-secondary-emphasis border border-secondary-subtle';
}

function normalize_action_details(log = {}) {
  const actions = [
    log?.action_taken,
    log?.action,
    log?.transition_display,
    log?.event,
    log?.log_type,
    'Audit event',
  ];
  const raw_action = normalize_text(actions.find(Boolean));
  const action = normalize_action(raw_action);
  const diff = diff_label(log, raw_action);
  return {
    action,
    action_label: friendly_action_label(action, diff),
    diff,
    event_key: event_key_for_log(log, action, diff),
  };
}

function normalize_audit_entry(log = {}, index = 0, case_lookup = new Map()) {
  const case_context = case_context_for_log(log, case_lookup);
  const routed_to = routed_to_label(log);
  const action_details = normalize_action_details(log);
  const diff = replace_routed_user_count(action_details.diff, routed_to);
  const status = status_label(log);
  const actor = actor_label(log);
  const actor_role = actor_role_label(log);
  const actor_email = actor_email_label(log);
  return {
    id: log?.id || `${action_details.action}-${index}`,
    time: normalize_text(log?.time || format_audit_timestamp(audit_timestamp_raw(log))),
    actor,
    actor_key: actor_key(log, actor),
    actor_role,
    actor_email,
    actor_filter_key: actor_filter_key(actor, actor_role, actor_email),
    ...action_details,
    diff,
    action_badge_class: action_badge_class(action_details.event_key, action_details.action),
    status,
    status_key: status_key(status),
    status_badge_class: status_badge_class(status),
    target: target_label(log, case_lookup),
    case_route: case_route(log, case_lookup),
    case_routed_to: normalize_text(case_context?.routed_to),
    source: source_label(log, actor, diff),
    sort_time: audit_timestamp_value(log),
    raw: log,
  };
}

export function normalize_entries(logs = [], case_lookup = new Map()) {
  const entries = logs
    .map((log, index) => normalize_audit_entry(log, index, case_lookup))
    .filter((entry) => entry.action || entry.diff)
    .sort((left, right) => Number(right.sort_time || 0) - Number(left.sort_time || 0));

  return enrich_reply_action_details(enrich_routed_assignment_details(entries));
}

function enrich_reply_action_details(entries = []) {
  return entries.map((entry, index) => {
    if (entry.action_label !== 'Mention Created' || !is_reply_to_previous_mention(entry, index, entries)) {
      return entry;
    }

    return {
      ...entry,
      action_label: 'Reply Created',
    };
  });
}

function is_reply_to_previous_mention(entry = {}, index = 0, entries = []) {
  const mentioned_name = leading_mentioned_name(entry.diff);
  if (!mentioned_name) return false;

  return entries
    .slice(index + 1)
    .some((older_entry) =>
      same_case_target(entry, older_entry)
      && names_match(older_entry.actor, mentioned_name)
      && names_match(leading_mentioned_name(older_entry.diff), entry.actor)
    );
}

function leading_mentioned_name(details = '') {
  const body = journal_entry_body(details) || normalize_text(details);
  const match = body.match(/@([^.,;:!?\n]+)/);
  return normalize_text(match?.[1]);
}

function same_case_target(left = {}, right = {}) {
  const left_target = normalize_text(left.target);
  const right_target = normalize_text(right.target);
  return left_target && right_target && left_target === right_target;
}

function names_match(left = '', right = '') {
  const left_name = normalize_text(left).toLowerCase();
  const right_name = normalize_text(right).toLowerCase();
  return Boolean(left_name && right_name && (left_name === right_name || left_name.includes(right_name) || right_name.includes(left_name)));
}

function enrich_routed_assignment_details(entries = []) {
  const assignment_entries = entries
    .map((entry, index) => ({ ...entry, index, assignee_name: assignment_assignee_name(entry) }))
    .filter((entry) => entry.assignee_name);

  return entries.map((entry, index) => {
    if (!needs_assignment_name(entry)) return entry;

    const assignment_entry = matching_assignment_entry(entry, index, assignment_entries);
    const assignee_name = assignment_entry?.assignee_name || entry.case_routed_to;
    if (!assignee_name) return entry;

    return {
      ...entry,
      target: entry.target === '-' && assignment_entry?.target ? assignment_entry.target : entry.target,
      diff: replace_team_member_label(entry.diff, assignee_name),
    };
  });
}

function needs_assignment_name(entry = {}) {
  return /\bsent to (?:one|\d+) team member/i.test(entry?.diff || '');
}

function assignment_assignee_name(entry = {}) {
  const text = normalize_text([
    entry?.diff,
    entry?.raw?.details,
    entry?.raw?.message,
    entry?.raw?.note,
    entry?.raw?.description,
  ].find(Boolean));
  const match = text.match(/application assigned to\s+(.+?)(?:\.|$)/i)
    || text.match(/\bassigned to\s+(.+?)(?:\.|$)/i);

  return match ? normalize_text(match[1]) : '';
}

function matching_assignment_entry(entry = {}, index = 0, assignment_entries = []) {
  const by_target = entry.target && entry.target !== '-'
    ? assignment_entries.find((assignment) =>
      assignment.target === entry.target && is_nearby_audit_event(entry, assignment)
    )
    : null;

  if (by_target) return by_target;

  return assignment_entries.find((assignment) =>
    assignment.index > index
    && same_audit_actor(entry, assignment)
    && is_nearby_audit_event(entry, assignment)
  );
}

function replace_team_member_label(details = '', assignee_name = '') {
  return normalize_text(details).replace(
    /\bsent to (?:one|\d+) team member(?:s)?/i,
    `sent to ${assignee_name}`
  );
}

function same_audit_actor(left = {}, right = {}) {
  return normalize_text(left.actor_key || left.actor).toLowerCase()
    === normalize_text(right.actor_key || right.actor).toLowerCase();
}

function is_nearby_audit_event(left = {}, right = {}) {
  const left_time = Number(left.sort_time || 0);
  const right_time = Number(right.sort_time || 0);
  return left_time && right_time ? Math.abs(left_time - right_time) <= 60000 : left.time === right.time;
}

function range_days(range = '') {
  const match = normalize_text(range).match(/(\d+)/);
  return match ? Number(match[1]) : null;
}

export function entry_in_range(entry = {}, range = '') {
  const days = range_days(range);
  if (!days || !entry.sort_time) return true;

  const cutoff = Date.now() - (days * 24 * 60 * 60 * 1000);
  return Number(entry.sort_time) >= cutoff;
}

export function audit_error_message(error) {
  return error?.response?.data?.message
    || error?.response?.data?.detail
    || error?.message
    || 'Failed to load audit log.';
}
